/**
 * Dynamic Product Card Renderer
 * Replicates the Liquid product-card snippet for JS-rendered content
 */

(function() {
  'use strict';

  function formatPrice(amount, currencyCode = 'EUR') {
    const price = parseFloat(amount);
    if (isNaN(price)) return '';

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(price);
  }

  function getProductUrl(product) {
    if (product.url) return product.url;
    if (product.handle) return `/products/${product.handle}`;
    return '#';
  }

  function getProductImage(product) {
    if (product.cleanImageUrl) return product.cleanImageUrl;
    if (product.images && product.images.length > 0) {
      return product.images[0].url || product.images[0];
    }
    if (product.featuredImage) return product.featuredImage.url || product.featuredImage;
    return null;
  }

  function getProductImageAlt(product) {
    if (product.images && product.images.length > 0 && product.images[0].altText) {
      return product.images[0].altText;
    }
    return product.title || '';
  }

  function createProductCard(product, options = {}) {
    const {
      impressionContext = 'recommendations',
      lazy = true,
      showPrice = true,
      showFeedback = true,
      enableHoverImages = false,
      hoverImagesCount = 2
    } = options;

    const card = document.createElement('product-card');
    card.className = 'product-card text-left product-card--border-false';

    if (product.id) {
      card.dataset.productGid = product.id;
    }
    if (product.handle) {
      card.dataset.productHandle = product.handle;
    }
    if (impressionContext) {
      card.dataset.impressionContext = impressionContext;
    }

    const url = getProductUrl(product);
    const imageUrl = getProductImage(product);
    const imageAlt = getProductImageAlt(product);
    const price = product.price;
    const compareAtPrice = product.compareAtPrice;
    const canAddToCart = product.availableForSale !== false;
    const feedbackState = product.feedback || null;

    const stockInfo = createStockInfoHtml(product);

    // Get secondary images for hover
    // If cleanImageUrl is used as primary, hover shows Shopify images (even just 1)
    // Otherwise, need at least 2 images for hover to make sense
    const allImages = product.images || [];
    const usesCleanImage = !!product.cleanImageUrl;
    const hasHoverImages = enableHoverImages && (
      (usesCleanImage && allImages.length >= 1) ||
      (!usesCleanImage && allImages.length > 1)
    );
    const secondaryImages = hasHoverImages ? allImages.slice(0, hoverImagesCount + 1) : [];
    const hoverClass = hasHoverImages ? ' thb-hover' : '';

    card.innerHTML = `
      <figure class="product-card-image${hoverClass}">
        <a href="${url}" title="${escapeHtml(product.title)}" class="product-card-image-link aspect-ratio aspect-ratio--square" style="--padding-bottom: 100%;">
          ${createImageHtml(imageUrl, imageAlt, lazy)}
          ${hasHoverImages ? createSecondaryImagesHtml(secondaryImages, lazy) : ''}
        </a>
        ${hasHoverImages ? createImageNavHtml(secondaryImages.length) : ''}
      </figure>
      <div class="product-card-info">
        <div class="product-card-info--inner">
          ${showPrice && price ? createPriceHtml(price, compareAtPrice) : ''}
          <a href="${url}" title="${escapeHtml(product.title)}" class="product-card-title">${escapeHtml(product.title)}</a>
          ${stockInfo}
          ${showFeedback ? createFeedbackButtonsHtml(feedbackState) : ''}
        </div>
        <div class="product-card-info--bottom">
          ${canAddToCart ? `
            <button type="button" class="button small product-card--add-to-cart" data-handle="${escapeHtml(product.handle)}">
              <span class="add-to-cart-text">Ajouter au panier</span>
              <span class="add-to-cart-loading" style="display:none;">Ajout...</span>
            </button>
          ` : `
            <button type="button" class="button small product-card--add-to-cart" disabled>Épuisé</button>
          `}
          <a href="${url}" class="button outline small product-card--view-details">Voir le produit</a>
        </div>
      </div>
    `;

    setupCardEventListeners(card);

    return card;
  }

  function createStockInfoHtml(product) {
    const available = product.availableForSale !== false;
    const inventory = product.totalInventory;
    const threshold = 5;

    if (!available || inventory === 0) {
      return '';
    }

    let notice = '';
    let isLow = false;

    if (inventory === 1) {
      notice = 'Dernier exemplaire disponible';
      isLow = true;
    } else if (inventory !== undefined && inventory <= threshold) {
      notice = `${inventory} en stock`;
      isLow = true;
    } else {
      notice = 'En stock';
    }

    const lowClass = isLow ? ' product-inventory-notice--low' : '';

    return `
      <div class="product-inventory-notice--wrapper">
        <div class="product-inventory-notice${lowClass}">
          <div class="product-inventory-notice--text">
            <div class="product-inventory-notice--pulse"></div>
            ${notice}
          </div>
        </div>
      </div>
    `;
  }

  function createFeedbackButtonsHtml(feedbackState) {
    const likeActive = feedbackState === 'like' ? ' active' : '';
    const dislikeActive = feedbackState === 'dislike' ? ' active' : '';

    return `
      <div class="product-card-feedback">
        <button type="button" class="feedback-btn feedback-like${likeActive}" data-feedback="like" title="J'aime">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
          </svg>
          <span>J'aime</span>
        </button>
        <button type="button" class="feedback-btn feedback-dislike${dislikeActive}" data-feedback="dislike" title="Je n'aime pas">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
          </svg>
          <span>Je n'aime pas</span>
        </button>
      </div>
    `;
  }

  function setupCardEventListeners(card) {
    const addToCartBtn = card.querySelector('.product-card--add-to-cart');
    if (addToCartBtn && !addToCartBtn.disabled) {
      addToCartBtn.addEventListener('click', handleAddToCart);
    }

    const feedbackBtns = card.querySelectorAll('.feedback-btn');
    feedbackBtns.forEach(btn => {
      btn.addEventListener('click', handleFeedback);
    });

    // Setup hover images manually (ProductCard connectedCallback runs before innerHTML is set)
    setupHoverImages(card);
  }

  function setupHoverImages(card) {
    const images = card.querySelectorAll('.product-secondary-image');
    const nav = card.querySelectorAll('.product-secondary-images-nav li');
    const imageContainer = card.querySelector('.product-card-image');

    if (!images.length || !imageContainer) return;

    const imageLength = images.length;

    const mousemove = function(e) {
      const l = e.offsetX;
      const w = this.getBoundingClientRect().width;
      const prc = l / w;
      const sel = Math.floor(prc * imageLength);
      const selimg = images[sel];

      images.forEach((image, index) => {
        if (image.classList.contains('hover')) {
          image.classList.remove('hover');
          if (nav.length) {
            nav[index].classList.remove('active');
          }
        }
      });

      if (selimg && !selimg.classList.contains('hover')) {
        selimg.classList.add('hover');
        if (nav.length && nav[sel]) {
          nav[sel].classList.add('active');
        }
      }
    };

    const mouseleave = function() {
      images.forEach((image, index) => {
        image.classList.remove('hover');
        if (nav.length && nav[index]) {
          nav[index].classList.remove('active');
        }
      });
    };

    imageContainer.addEventListener('mouseenter', mousemove, { passive: true });
    imageContainer.addEventListener('mousemove', mousemove, { passive: true });
    imageContainer.addEventListener('mouseleave', mouseleave, { passive: true });
    imageContainer.addEventListener('touchstart', mousemove, { passive: true });
    imageContainer.addEventListener('touchmove', mousemove, { passive: true });
    imageContainer.addEventListener('touchend', mouseleave, { passive: true });
  }

  async function handleAddToCart(e) {
    const btn = e.currentTarget;
    const handle = btn.dataset.handle;
    if (!handle || btn.disabled) return;

    const textEl = btn.querySelector('.add-to-cart-text');
    const loadingEl = btn.querySelector('.add-to-cart-loading');

    btn.disabled = true;
    if (textEl) textEl.style.display = 'none';
    if (loadingEl) loadingEl.style.display = 'inline';

    try {
      const productRes = await fetch(`/products/${handle}.js`);
      const productData = await productRes.json();

      const variant = productData.variants.find(v => v.available) || productData.variants[0];
      if (!variant) throw new Error('No variant available');

      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });

      if (!cartRes.ok) throw new Error('Failed to add to cart');

      if (textEl) textEl.textContent = 'Ajouté !';
      setTimeout(() => {
        if (textEl) textEl.textContent = 'Ajouter au panier';
      }, 2000);

      // Refresh cart content and open drawer (Pinnacle theme)
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        cartDrawer.refresh ? cartDrawer.refresh() : document.dispatchEvent(new CustomEvent('cart:refresh'));
        cartDrawer.open();
      }

    } catch (error) {
      console.error('[ProductCard] Add to cart failed:', error);
      if (textEl) textEl.textContent = 'Erreur';
      setTimeout(() => {
        if (textEl) textEl.textContent = 'Ajouter au panier';
      }, 2000);
    } finally {
      btn.disabled = false;
      if (textEl) textEl.style.display = 'inline';
      if (loadingEl) loadingEl.style.display = 'none';
    }
  }

  function getAllProductGidsOnPage(excludeGid) {
    const cards = document.querySelectorAll('product-card[data-product-gid]');
    const gids = [];
    cards.forEach(card => {
      const gid = card.dataset.productGid;
      if (gid && gid !== excludeGid) {
        gids.push(gid);
      }
    });
    return gids;
  }

  function replaceProductCard(oldCard, newProduct, context) {
    const options = {
      impressionContext: context || oldCard.dataset.impressionContext || 'recommendations',
      lazy: true,
      showPrice: true,
      showFeedback: true,
      enableHoverImages: oldCard.closest('[data-layout="grid"]') !== null,
      hoverImagesCount: 2
    };

    const newCard = createProductCard(newProduct, options);
    oldCard.replaceWith(newCard);
  }

  async function handleFeedback(e) {
    const btn = e.currentTarget;
    const card = btn.closest('product-card');
    const productGid = card?.dataset.productGid;
    const feedbackType = btn.dataset.feedback;

    if (!productGid || !feedbackType) return;

    const visitorId = window._rm && typeof window._rm.gv === 'function'
      ? await window._rm.gv()
      : null;

    if (!visitorId) {
      console.warn('[ProductCard] No visitor ID for feedback');
      return;
    }

    const isActive = btn.classList.contains('active');
    const newFeedback = isActive ? 'remove' : feedbackType;

    const siblingBtn = btn.parentElement.querySelector(
      feedbackType === 'like' ? '.feedback-dislike' : '.feedback-like'
    );

    btn.disabled = true;
    if (siblingBtn) siblingBtn.disabled = true;

    let loadingOverlay = null;
    if (feedbackType === 'dislike' && !isActive) {
      loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'product-card-loading-overlay';
      loadingOverlay.innerHTML = `
        <div class="product-card-loading-content">
          <div class="product-card-loading-spinner"></div>
          <span>On vous trouve une meilleure miniature...</span>
        </div>
      `;
      card.style.position = 'relative';
      card.appendChild(loadingOverlay);
    }

    try {
      const requestBody = { visitorId, productGid, feedback: newFeedback };

      if (feedbackType === 'dislike' && !isActive) {
        requestBody.currentProductGids = getAllProductGidsOnPage(productGid);
      }

      const res = await fetch('https://bernard-recommender.bernard-legoff-29.workers.dev/functions/v1/track-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) throw new Error('Feedback API error: ' + res.status);

      const data = await res.json();

      if (feedbackType === 'like') {
        if (isActive) {
          btn.classList.remove('active');
        } else {
          btn.classList.add('active');
          if (siblingBtn) siblingBtn.classList.remove('active');
        }
      } else if (feedbackType === 'dislike' && !isActive) {
        if (data.replacement) {
          replaceProductCard(card, data.replacement, card.dataset.impressionContext);
        } else {
          card.style.display = 'none';
        }
      } else if (isActive) {
        btn.classList.remove('active');
      }

    } catch (error) {
      console.error('[ProductCard] Feedback failed:', error);
      if (loadingOverlay) loadingOverlay.remove();
    } finally {
      btn.disabled = false;
      if (siblingBtn) siblingBtn.disabled = false;
    }
  }

  function createImageHtml(imageUrl, alt, lazy) {
    if (!imageUrl) {
      return `
        <div class="thb-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
            <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.5-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"/>
          </svg>
        </div>
      `;
    }

    const sizes = [375, 770];
    const srcset = sizes.map(w => `${resizeImage(imageUrl, w)} ${w}w`).join(', ');
    const placeholderSrc = resizeImage(imageUrl, 20);
    const defaultSrc = resizeImage(imageUrl, sizes[0]);

    if (lazy) {
      return `
        <img
          class="lazyload product-primary-image"
          data-sizes="auto"
          data-src="${placeholderSrc}"
          data-srcset="${srcset}"
          src="${placeholderSrc}"
          alt="${escapeHtml(alt)}"
          fetchpriority="auto"
        />
      `;
    }

    return `
      <img
        class="no-blur product-primary-image"
        src="${defaultSrc}"
        srcset="${srcset}"
        sizes="(max-width: 768px) 50vw, 33vw"
        alt="${escapeHtml(alt)}"
        loading="eager"
        fetchpriority="auto"
      />
    `;
  }

  function createSecondaryImagesHtml(images, lazy) {
    if (!images || images.length === 0) return '';

    const sizes = [375, 770];

    return images.map((img, index) => {
      const imageUrl = img.url || img;
      const alt = img.altText || '';
      const srcset = sizes.map(w => `${resizeImage(imageUrl, w)} ${w}w`).join(', ');
      const placeholderSrc = resizeImage(imageUrl, 20);

      return `
        <img
          class="lazyload product-secondary-image"
          data-sizes="auto"
          data-src="${placeholderSrc}"
          data-srcset="${srcset}"
          src="${placeholderSrc}"
          alt="${escapeHtml(alt)}"
          fetchpriority="low"
        />
      `;
    }).join('');
  }

  function createImageNavHtml(imageCount) {
    if (imageCount === 0) return '';

    const items = Array.from({ length: imageCount }, () => '<li></li>').join('');
    return `<ul class="product-secondary-images-nav">${items}</ul>`;
  }

  function createPriceHtml(price, compareAtPrice) {
    const currentPrice = parseFloat(price.amount || price);
    const formattedPrice = formatPrice(currentPrice, price.currencyCode || 'EUR');

    let html = '<span class="price">';

    if (compareAtPrice) {
      const comparePrice = parseFloat(compareAtPrice.amount || compareAtPrice);
      if (comparePrice > currentPrice) {
        html += `
          <del>
            <span class="amount discounted">${formatPrice(comparePrice, compareAtPrice.currencyCode || 'EUR')}</span>
          </del>
        `;
      }
    }

    html += `<ins><span class="amount">${formattedPrice}</span></ins>`;
    html += '</span>';

    return html;
  }

  function resizeImage(url, width) {
    if (!url) return '';

    if (url.includes('cdn.shopify.com')) {
      const sizeParam = `${width}x`;

      if (url.includes('_')) {
        return url.replace(/(_\d+x\d*)|(_\d+x)/, `_${sizeParam}`);
      }

      const extensionMatch = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (extensionMatch) {
        const ext = extensionMatch[1];
        const query = extensionMatch[2] || '';
        return url.replace(new RegExp(`\\.${ext}(\\?.*)?$`, 'i'), `_${sizeParam}.${ext}${query}`);
      }
    }

    return url;
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function createSkeletonCard() {
    const card = document.createElement('div');
    card.className = 'product-card-skeleton';
    card.innerHTML = `
      <div class="product-card-skeleton__image"></div>
      <div class="product-card-skeleton__vendor"></div>
      <div class="product-card-skeleton__title"></div>
      <div class="product-card-skeleton__price"></div>
    `;
    return card;
  }

  function renderProductGrid(container, products, options = {}) {
    if (!container || !products || products.length === 0) return;

    container.innerHTML = '';

    products.forEach((product, index) => {
      const card = createProductCard(product, {
        ...options,
        lazy: index >= 6
      });
      container.appendChild(card);
    });

    triggerLazyLoad();
  }

  function triggerLazyLoad() {
    if (typeof window.lazySizes !== 'undefined') {
      window.lazySizes.autoSizer.checkElems();
    }

    requestAnimationFrame(() => {
      const lazyImages = document.querySelectorAll('img.lazyload');
      lazyImages.forEach(img => {
        if (img.dataset.srcset && !img.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });
    });
  }

  window.ProductCardDynamic = {
    create: createProductCard,
    createSkeleton: createSkeletonCard,
    renderGrid: renderProductGrid,
    formatPrice: formatPrice,
    triggerLazyLoad: triggerLazyLoad
  };

})();
